// Central router declaration.
// /patients and /clinical-lead have no sidebar nav yet — reachable by direct URL only.
// Cross-tab data (Withings roster + Platform API dashboard merge) reaches
// pages via the `useAppData` composition hook (hooks/useAppData.ts).
import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import {
  createBrowserRouter, useParams, useNavigate, useSearchParams, useLocation,
} from 'react-router';
import { useAppData } from './hooks/useAppData';
import { pickBoardParams, KEEP_BOARD_FILTERS_STATE } from './hooks/useDashboardTable';
import { useFetchOpenTasksForPatient } from './state/data/task';
import { usePatientRecord } from './state/data/patient';
import { getRoleConfig } from './data/roles';
import RootLayout from './components/RootLayout';
import AccessDashboard from './components/AccessDashboard';
import ClinicalLeadTab from './components/ClinicalLeadTab';
import EngageWorkspace from './components/EngageWorkspace';
import PatientsTab from './components/PatientsTab';
import CareTeamExceptions from './components/CareTeamExceptions';
import PatientDetail from './components/PatientDetail';
import ErrorBoundary from './components/ErrorBoundary';
import ChooseCareTeamPage from './components/ChooseCareTeamPage';
import { setLastSelectedProgramId } from './lib/selectedProgram';

// Adapters map the shared app data (useAppData) onto the props the tab components take.

// Dim backdrop behind the patient drawer. The dim layer itself is decoration (Escape
// and the panel are the real affordances, so it stays aria-hidden); `label` adds a
// sibling live region so a wait or a failure is announced rather than just spun.
function DrawerBackdrop({ onClick, label, hidden }: {
  onClick: () => void;
  label?: string;
  hidden?: boolean;
}) {
  const off = hidden ? { display: 'none' } : undefined;
  return (
    <>
      <div className="slide-over-backdrop" onClick={onClick} aria-hidden="true" style={off} />
      {label && (
        <div
          className="loading-center"
          role="status"
          style={{
            position: 'fixed', inset: 0, zIndex: 151, pointerEvents: 'none', ...off,
          }}
        >
          <div className="loading-spinner" />
          <span className="loading-center-text">{label}</span>
        </div>
      )}
    </>
  );
}

// Slide-over patient detail rendered as an overlay. Refresh-friendly: the URL
// is the state, so reloading the page reopens the panel for the same patient.
// Two modes, both URL-driven: over /team/:programid it's a path segment
// (/patient/:id, dropped on close); over /engage/:id it's a `?patient=1` query
// param (EngageRoute passes its own `onClose` to strip the param instead of
// navigating to /team/:programid, which wouldn't make sense from Engage).
function PatientSlideover({ withingsUserId, onClose }: { withingsUserId: string; onClose?: () => void }) {
  const {
    allPatients, dashboardData, userRole, currentUser,
  } = useAppData();
  const navigate = useNavigate();
  const panelRef = useRef<HTMLDivElement>(null);
  // While a patient still needs the first care-team review, show ONLY the review
  // modal (PatientDetail portals it to body) — the drawer stays hidden until the
  // review is completed. `gateResolved` gates the initial spinner so the drawer
  // never flashes open before we know which it is.
  const [reviewRequired, setReviewRequired] = useState(false);
  const [gateResolved, setGateResolved] = useState(false);

  const [panelTasks, setPanelTasks] = useState<Array<{
    withings_user_id?: string | number;
    owner_role?: string;
    status?: string;
    task_id?: string | number;
  }>>([]);

  // Patient-scoped open-task fetch (NOT the global staff queue, which is capped
  // at 100 across all patients — a patient past the cutoff would show no tasks
  // here while still surfacing on the server-paginated dashboard).
  const fetchOpenTasks = useFetchOpenTasksForPatient();
  const [tasksError, setTasksError] = useState(false);
  const refreshTasks = useCallback(async () => {
    try {
      const all = await fetchOpenTasks(withingsUserId, { assignedToType: 'staff' });
      setPanelTasks((all as typeof panelTasks) || []);
      setTasksError(false);
    } catch { setTasksError(true); }
  }, [fetchOpenTasks, withingsUserId]);

  useEffect(() => {
    let cancelled = false;
    setPanelTasks([]);
    setTasksError(false);
    fetchOpenTasks(withingsUserId, { assignedToType: 'staff' })
      .then((all) => {
        if (!cancelled) { setPanelTasks((all as typeof panelTasks) || []); setTasksError(false); }
      })
      .catch(() => { if (!cancelled) setTasksError(true); });
    return () => { cancelled = true; };
  }, [withingsUserId, fetchOpenTasks]);

  // Reset on patient switch (the file remounts; re-gate the next one).
  useEffect(() => { setReviewRequired(false); setGateResolved(false); }, [withingsUserId]);

  // PatientDetail reports the LIVE first-review decision — it can change after the
  // initial resolve (completing the review flips it false → reveal the drawer), so
  // track the latest value rather than latching once.
  const handleGateResolved = useCallback((needsReview: boolean) => {
    setGateResolved(true);
    setReviewRequired(needsReview);
  }, []);

  // Close = drop the /patient/:id segment, stay on the same /team/:programid.
  // The board's filters + sort ride along (useDashboardTable mirrors them onto the
  // query string, so dropping them would silently reset the operator's filtered
  // roster on close) — but ONLY those: `pickBoardParams` strips the drawer's own
  // params, so ?tab= doesn't follow us back onto the board. A caller mounting the
  // drawer over a non-/team route (e.g. Engage) passes its own `onClose` since
  // there's no /team/:programid to fall back to.
  const { programid } = useParams<{ programid?: string }>();
  const { search } = useLocation();
  const close = useCallback(
    () => (onClose
      ? onClose()
      : navigate({
        pathname: programid ? `/team/${programid}` : '/',
        search: pickBoardParams(search),
      }, { state: KEEP_BOARD_FILTERS_STATE })),
    [navigate, programid, onClose, search],
  );

  // Esc key closes + lock body scroll while the drawer is open — mirrors the
  // modal pattern used elsewhere in the app.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    // Move focus into the drawer only once it's actually revealed (visible) —
    // honors aria-modal for keyboard / screen-reader users. The panel is
    // display:none while the gate is unresolved OR the review modal owns the
    // screen; re-running on both flags catches the completed-review → drawer
    // reveal so focus follows.
    if (gateResolved && !reviewRequired) panelRef.current?.focus();
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [close, withingsUserId, gateResolved, reviewRequired]);

  const rosterPatient = (allPatients as Array<{ userid: string | number }>).find(
    (p) => String(p.userid) === String(withingsUserId),
  );
  const dashRow = (dashboardData?.rows || []).find(
    (r) => String((r as { userid: string | number }).userid) === String(withingsUserId),
  ) as {
    userid?: string | number;
    firstname?: string;
    lastname?: string;
    name?: { first?: string; last?: string };
    ehr_patient_id?: string | null;
  } | undefined;
  // Both lookups above cover only the board's FIRST page, so an off-page patient needs
  // the authoritative patients-table row. Same key as PatientFileShell → cache-deduped.
  const recordQuery = usePatientRecord(withingsUserId);

  // DB row without Withings roster entry — test_mode onboardings (synthetic UID
  // never registered with Withings) and production-orphaned patients (linkage
  // revoked). Treat the DB as the source of truth and seed `patient` from the
  // dashboard row.
  let patient: typeof rosterPatient | {
    userid: string | number;
    firstname: string;
    lastname: string;
    ehr_patient_id: string | null;
  } | undefined;
  if (!rosterPatient && dashRow) {
    patient = {
      userid: dashRow.userid ?? withingsUserId,
      firstname: dashRow.name?.first || dashRow.firstname || '',
      lastname: dashRow.name?.last || dashRow.lastname || '',
      ehr_patient_id: dashRow.ehr_patient_id || null,
    };
  } else if (!rosterPatient && recordQuery.data) {
    const rec = recordQuery.data;
    patient = {
      userid: rec.withings_user_id ?? withingsUserId,
      firstname: rec.first_name || '',
      lastname: rec.last_name || '',
      ehr_patient_id: rec.ehr_patient_id != null ? String(rec.ehr_patient_id) : null,
    };
  } else if (!rosterPatient && recordQuery.isPending) {
    // Off-page patient, record still in flight — never a dead click.
    return <DrawerBackdrop onClick={close} label="Loading patient…" />;
  } else if (!rosterPatient && recordQuery.isError) {
    // A failed fetch is not a missing patient — saying so beats vanishing, which is
    // indistinguishable from the off-page dead-click bug this fallback exists to fix.
    return (
      <DrawerBackdrop onClick={close} label="Couldn't load this patient. Close and try again." />
    );
  } else if (!rosterPatient) {
    // Stale URL — no roster entry, no dashboard row, no patients-table row.
    return null;
  } else {
    patient = dashRow
      ? {
        ...rosterPatient,
        firstname: dashRow.firstname || (rosterPatient as { firstname?: string }).firstname,
        lastname: dashRow.lastname || (rosterPatient as { lastname?: string }).lastname,
        ehr_patient_id: dashRow.ehr_patient_id || null,
      }
      : rosterPatient;
  }

  const roleCfg = getRoleConfig(userRole) as { taskRoles?: string[] };
  const roleTaskRoles = roleCfg?.taskRoles || [];
  const allPatientTasks = panelTasks.filter((t) => (
    String(t.withings_user_id) === String(withingsUserId)
    && (t.status === 'pending' || t.status === 'in_progress' || t.status === 'overdue' || t.status === 'scheduled')
  ));
  const roleTasks = panelTasks.filter((t) => (
    !!t.owner_role
    && roleTaskRoles.includes(t.owner_role)
    && String(t.withings_user_id) === String(withingsUserId)
    && (t.status === 'pending' || t.status === 'in_progress' || t.status === 'overdue' || t.status === 'scheduled')
  ));

  return (
    <>
      {/* The label doubles as the loading affordance while we decide
          drawer-vs-review-modal — keeps the backdrop from reading as a dead screen. */}
      <DrawerBackdrop
        onClick={close}
        hidden={reviewRequired}
        label={gateResolved ? undefined : 'Loading patient file…'}
      />
      {/* TODO(witems): witems SidePanel is fixed-px width; revisit if it gains responsive width. */}
      <div
        ref={panelRef}
        className="slide-over-panel"
        role="dialog"
        aria-modal="true"
        aria-label="Patient file"
        tabIndex={-1}
        style={(reviewRequired || !gateResolved) ? { display: 'none' } : undefined}
      >
        <div className="slide-over-body">
          {tasksError && (
            <div role="alert" style={{ padding: 16 }}>
              Open tasks could not be fully loaded. The task list may be incomplete; this does not mean the work is done.
              {' '}
              <button type="button" onClick={refreshTasks}>Retry loading tasks</button>
            </div>
          )}
          <ErrorBoundary key={withingsUserId} label="this patient">
            <PatientDetail
              patient={patient}
              userRole={userRole}
              tasks={roleTasks}
              tasksError={tasksError}
              allTasksForPatient={allPatientTasks}
              currentUser={currentUser}
              onRefreshTasks={refreshTasks}
              onReviewGateResolved={handleGateResolved}
              onClose={close}
              onOpenEngage={() => navigate(`/engage/${String(withingsUserId)}`)}
            />
          </ErrorBoundary>
        </div>
      </div>
    </>
  );
}

function HomeRoute() {
  const { programid, withingsUserId } = useParams<{ programid: string; withingsUserId?: string }>();
  const {
    // Base role setter (not RootLayout's routed wrapper): home is in ROLE_TABS for
    // both roles, so no tab-redirect is needed — it just re-scopes the dashboard.
    allPatients, dashboardData, userRole, currentUser, handleRoleChange,
  } = useAppData();
  const navigate = useNavigate();
  const { search } = useLocation();

  // Remember the last team the user opened. Drives the sidebar Home
  // nav target on subsequent visits from non-team routes (engage, `/`).
  // See `lib/selectedProgram.ts`.
  useEffect(() => {
    if (programid) setLastSelectedProgramId(programid);
  }, [programid]);

  return (
    <>
      <AccessDashboard
        allPatients={allPatients as unknown[]}
        dashboardData={dashboardData}
        userRole={userRole}
        onRoleChange={handleRoleChange}
        userName={
          (currentUser as { firstname?: string; shortname?: string } | null)?.firstname
          || (currentUser as { firstname?: string; shortname?: string } | null)?.shortname
          || null
        }
        onSelectPatient={(userid: string | number) => {
          // Navigate to /team/:programid/patient/:withingsUserId — HomeRoute
          // absorbs the URL via useParams() and renders the slide-over below
          // over the dashboard. Same programid is preserved — and so are the
          // board's filters + sort (useDashboardTable reads them back OFF the URL,
          // so dropping them would clear the roster the moment a patient is
          // opened). The drawer opens on its default tab, so board params only — never the
          // diagnosis-level ones next to a patient id; the board keeps those in its state.
          navigate({
            pathname: `/team/${programid}/patient/${String(userid)}`,
            search: pickBoardParams(search, { forPatient: true }),
          });
        }}
      />
      {withingsUserId && <PatientSlideover withingsUserId={withingsUserId} />}
    </>
  );
}

function EngageRoute() {
  const { withingsUserId } = useParams<{ withingsUserId?: string }>();
  const { userRole, currentUser } = useAppData();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Selection is the URL. Clicking a row pushes /engage/:id (shareable +
  // refresh-stable); the sidebar Engage nav lands on bare /engage (no selection).
  const onSelectPatient = useCallback(
    (id: string | number | null) => navigate(id ? `/engage/${String(id)}` : '/engage'),
    [navigate],
  );
  // Patient file drawer over Engage: `?patient=1` on the same /engage/:id URL
  // (refresh-stable, same URL-is-state idiom as the /team/:programid/patient
  // overlay). Opening/closing preserves the chat selection.
  const showPatientFile = withingsUserId != null && searchParams.get('patient') === '1';
  const openPatientFile = useCallback(() => {
    if (withingsUserId) navigate(`/engage/${withingsUserId}?patient=1`);
  }, [navigate, withingsUserId]);
  const closePatientFile = useCallback(() => {
    if (withingsUserId) navigate(`/engage/${withingsUserId}`);
  }, [navigate, withingsUserId]);
  return (
    <>
      <EngageWorkspace
        userRole={userRole}
        currentUser={currentUser}
        selectedPatientId={withingsUserId ?? null}
        onSelectPatient={onSelectPatient}
        onOpenPatientFile={openPatientFile}
      />
      {showPatientFile && (
        <PatientSlideover withingsUserId={withingsUserId as string} onClose={closePatientFile} />
      )}
    </>
  );
}

function PatientsRoute() {
  const { allPatients } = useAppData();
  return <PatientsTab allPatients={allPatients as unknown[]} />;
}

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      // `/` → care-team picker (auto-redirects to /team/:programid when
      // a single Access-flavored program matches — the common case).
      // `/team/:programid` and `/team/:programid/patient/:withingsUserId`
      // both resolve to HomeRoute; the latter overlays PatientSlideover
      // on top of the dashboard, URL-driven so refresh keeps the panel
      // open on the same patient. The /patients/:id legacy URL is gone
      // — clean break, no backward-compat redirect.
      { path: '/', element: <ChooseCareTeamPage /> },
      { path: '/team/:programid', element: <HomeRoute /> },
      { path: '/team/:programid/exceptions', element: <CareTeamExceptions /> },
      { path: '/team/:programid/patient/:withingsUserId', element: <HomeRoute /> },
      { path: '/patients', element: <PatientsRoute /> },
      // Both resolve to EngageRoute; the :withingsUserId variant selects that
      // patient's chat and makes the URL shareable / refresh-stable.
      { path: '/engage', element: <EngageRoute /> },
      { path: '/engage/:withingsUserId', element: <EngageRoute /> },
      { path: '/clinical-lead', element: <ClinicalLeadTab /> },
    ],
  },
]);
